import classNames from 'classnames';
import { connect } from 'react-redux';

import { numberUtils } from 'app-components/utils/number';

import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import QTip from 'components/QuickenControls/QTip';
import getSymbolFromCurrency from 'currency-symbol-map';
import numeral from 'numeral';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { withStyles } from 'tss-react/mui';
import { exists, isBrowser, noNaN } from 'utils/utils';
import selectPrivacyModeEnabled from 'app-components/hooks/privacyMode/selectPrivacyModeEnabled';

const { internalFormatNumber } = numberUtils;

export const { ShowSignEnum } = numberUtils;

export const ShowColorEnum = Object.freeze({
  MANUAL: 'manual',
  NEGATIVE_ONLY: 'negative-only',
  POSITIVE_ONLY: 'positive-only',
  ALWAYS: 'always',
});

const styles = (theme) => ({
  inputField: {
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  typographyField: {
    whiteSpace: 'nowrap',
  },
  red: {
    color: `${theme.palette.amount.negative} !important`,
    whiteSpace: 'nowrap',
  },
  green: {
    color: `${theme.palette.amount.positive} !important`,
    whiteSpace: 'nowrap',
  },
  black: {
    color: `${theme.palette.text.primary} !important`,
    whiteSpace: 'nowrap',
  },
  white: {
    color: theme.palette.primary.contrastText,
    whiteSpace: 'nowrap',
  },
  primary: {
    color: theme.palette.primary.main,
    whiteSpace: 'nowrap',
  },
  secondary: {
    color: theme.palette.secondary.main,
    whiteSpace: 'nowrap',
  },
  error: {
    color: theme.palette.primary.error,
    whiteSpace: 'nowrap',
  },
  amountadornment: {
    marginLeft: 0,
    marginRight: 4,
  },
  privacyMask: {
    WebkitTextSecurity: 'disc',
  },
});

//
// Given a user provided amount, proposedSign is either 1 or -1
//
function quickenAmountToString(amountStr, proposedSign = 1) {
  // this just safety checks the value to ensure it is either 1 or -1
  let sign = (proposedSign / proposedSign || 1) * Math.sign(proposedSign) || 1;

  if (String(amountStr).charAt(0) === '+') {
    sign = 1;
  }
  if (String(amountStr).charAt(0) === '-') {
    sign = -1;
  }
  return numeral(Math.abs(Number(amountStr)) * sign).format('0.00');
}

export function round(value, decimals = 2) {
  const roundedValue = Math.round(Number(`${value}e${decimals}`));
  return Number(`${roundedValue}e-${decimals}`);
}

export class AmountField extends PureComponent {
  constructor(props) {
    super(props);

    const currencyString = (getSymbolFromCurrency(props.currencySymbol || 'USD') || '').trim();
    this.state = {
      currencyString: !props.hideCurrencyString ? currencyString : '',
    };
    this.proposedSign = props.proposedSign;

    const val = this.valueFromProps(props);
    this.state.value = props.minVal > val ? props.minVal : val;
    this.initialValue = props.minVal > val ? props.minVal : val;

    this.selection = {
      start: false,
      end: false,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!Number.isNaN(Number(nextProps.value)) || !Number.isNaN(Number(nextProps.updateValue))) {
      let newVal = nextProps.value || nextProps.updateValue || '';

      newVal = this.validateField(String(newVal), true);

      if (Number(newVal) !== Number(this.state.value)) {
        this.setState({ value: newVal });
      }
    }
    if (nextProps.proposedSign !== this.proposedSign && !nextProps.lockInitialProposedSign) {
      this.proposedSign = nextProps.proposedSign;
    }
    if (nextProps.currencySymbol !== this.props.currencySymbol) {
      this.setState({ currencyString: (getSymbolFromCurrency(nextProps.currencySymbol || 'USD') || '').trim() });
    }
    if (nextProps.editable && nextProps.editable !== this.props.editable) {
      const val = this.valueFromProps(nextProps);
      this.setState({ value: val });
    }
  }

  componentDidUpdate() {
    if (this.textInput) {
      const { selectionStart, selectionEnd } = this.textInput;
      const update =
        (this.selection.start !== false && this.selection.start !== selectionStart) ||
        (this.selection.end !== false && this.selection.end !== selectionEnd);

      if (update && !isBrowser('safari')) {
        this.textInput.setSelectionRange(this.selection.start, this.selection.end);
      }
    }
  }

  valueFromProps = (props) => {
    let val = numeral(props.value || props.updateValue || '');
    if (props.editable && props.value == null) {
      val = '';
    } else {
      val = val.format(
        `${props.showSign === ShowSignEnum.ALWAYS ? '+-' : ''}0,00.00${
          this.props.fractionDigits <= 2 ? '' : `[${'0'.repeat(this.props.fractionDigits - 2)}]`
        }`,
      );
      val = this.validateField(val, true);
    }
    return val;
  };

  onBlur = (e, wasEnter) => {
    const { maxVal, minVal } = this.props;

    if (this.validateField(e.target.value).trim().length > 0) {
      let newVal = this.validateField(e.target.value);

      const valChanged = Number(newVal) !== Number(this.initialValue);
      const absValChanged = Math.abs(Number(newVal)) !== Math.abs(Number(this.initialValue));

      if ((typeof maxVal !== 'number' || newVal <= maxVal) && (typeof minVal !== 'number' || newVal >= minVal)) {
        if (this.props.onBlur) {
          if (valChanged) {
            newVal = quickenAmountToString(
              newVal,
              absValChanged && (this.proposedSign || Math.sign(this.initialValue)),
            );
          }
          e.target.value = newVal;
          this.props.onBlur(e, this.props.amountType, wasEnter);
        }

        this.setState({ value: newVal });
      } else if (e.target.value === '' && this.props.onBlur) {
        this.props.onBlur(e, this.props.amountType, wasEnter);
      }

      if (this.props.submitOnBlur && this.props.onSubmit) {
        this.props.onSubmit();
      }
    }
  };

  setInputRef = (el) => {
    this.textInput = el;
    if (this.props.inputRef) {
      this.props.inputRef(el);
    }
  };

  handleChange = (e) => {
    const { maxVal, minVal } = this.props;

    if (this.textInput) {
      this.selection = {
        start: this.textInput.selectionStart,
        end: this.textInput.selectionEnd,
      };
    }

    let newVal = this.validateField(e.target.value);

    if ((typeof maxVal !== 'number' || newVal <= maxVal) && (typeof minVal !== 'number' || newVal >= minVal)) {
      this.setState({ value: newVal });

      if (this.props.useAbsoluteValue) {
        // make negative
        newVal = `-${newVal}`;
      }

      e.target.value = newVal;

      if (this.props.onChange) {
        this.props.onChange(e, this.props.amountType);
      }
    }
  };

  validateField = (value, noStrip = false) => {
    // only allow 2 decimals in amount field, and only 1 decimal point
    // need to give each field their own handler, and then have a default handler

    let str = '';
    let newVal = noStrip ? value : value.replace('$', '').trim();

    const legalChars =
      this.props.amountType === 'amount' || this.props.amountType === 'balance' ? '0123456789.-+' : '0123456789.';

    // strip out illegal characters (only numbers and decimals)
    for (let i = 0; i < newVal.length; i += 1) {
      str += legalChars.indexOf(newVal[i]) < 0 ? '' : newVal[i];
    }

    newVal = str;

    // This code will ensure there are only #fractionDigits digits to the right of the decimal
    if (str) {
      if (str.indexOf('.') >= 0 && str.length > 1) {
        if (
          str.length - str.indexOf('.') - 1 > this.props.fractionDigits ||
          value.indexOf('.') !== value.lastIndexOf('.')
        ) {
          newVal = parseFloat(Number(str).toFixed(this.props.fractionDigits)).toString();
        }
      }
    }

    return newVal;
  };

  handleFocus = (e) => {
    const numCharsToStrip = this.state.currencyString ? this.state.currencyString.length + 1 : 0;

    const { target } = e;
    setTimeout(() => {
      if (this.props.showAmountAdornment) {
        target.select();
      } else {
        target.setSelectionRange(numCharsToStrip, 99);
      }
    }, 0);

    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
    this.initialValue = this.state.value;
  };

  // deduce the string for the static value state
  renderStaticValue = (value, currencyString, omitCents, showSign) => {
    if (!exists(value) || (this.props.zeroIsBlank && noNaN(value) === 0)) {
      return <span>{'\u00A0'}</span>; // Danger, u00A0 may be removed from browser support.
    }
    return internalFormatNumber(
      value,
      `${currencyString}${currencyString && this.props.currencyGap ? ' ' : ''}`,
      `0,0${
        omitCents ? '' : `.00${this.props.fractionDigits <= 2 ? '' : `[${'0'.repeat(this.props.fractionDigits - 2)}]`}`
      }${this.props.compact ? 'a' : ''}`,
      showSign,
      this.props.fractionCents,
      this.props.fractionDigits,
    );
  };

  handleKeyPress = (event) => {
    if (event.key === '.' && this.state.value.indexOf('.') >= 0) {
      // this is so that you can reset a selection with a period if a period is already selected
      const subStr = this.state.value.substring(this.textInput.selectionStart, this.textInput.selectionEnd);
      const hasPeriod = subStr.indexOf('.') >= 0;
      if (!hasPeriod) {
        event.stopPropagation();
        event.preventDefault();
      }
    }
    if (event.key === 'Enter') {
      // if user hits Enter, treat like TAB and treat as a blur to capture the value
      const e = { target: { value: `${this.state.currencyString} ${this.state.value}` } };
      this.onBlur(e, true);

      if (this.props.onSubmit) {
        this.props.onSubmit();
      }
    }
    if (this.props.onKeyDown) {
      this.props.onKeyDown(event);
    }
  };

  tooltip() {
    if (this.props.compact /* || this.props.omitCents */) {
      return internalFormatNumber(
        this.props.value,
        this.state.currencyString,
        '0,0.00',
        this.props.showSign,
        this.props.fractionCents,
      );
    }
    return '';
  }

  /* eslint-disable @typescript-eslint/no-unused-vars */
  // too many props that need to be excluded from the '...others' spread
  render() {
    const {
      currencySymbol,
      inputRef,
      showColor,
      color,
      classes,
      editable,
      thinFont,
      lockInitialProposedSign,
      amountType,
      value,
      updateValue,
      useAbsoluteValue,
      zeroIsBlank,
      omitCents,
      currencyGap,
      className,
      proposedSign,
      disableUnderline,
      submitOnBlur,
      onSubmit,
      showAmountAdornment,
      autoFocus,
      compact,
      hideCurrencyString,
      variant,
      textFieldVariant,
      marginProp,
      label,
      maxChars,
      showSign,
      minVal,
      maxVal,
      fractionCents,
      fractionDigits,
      textPrimary,
      textSecondary,
      isPrivacyMode,
      dispatch,
      ...other
    } = this.props;

    const { currencyString } = this.state;

    const Wrap = compact ? QTip : React.Fragment;
    const wrapProps = compact ? { title: this.tooltip() } : {};

    // non-standard attribute, we should not always rely on it
    const supportsWebkitTextSecurity =
      typeof window !== 'undefined' && window.CSS?.supports?.('-webkit-text-security', 'disc');

    const privacyModeProps =
      isPrivacyMode && !supportsWebkitTextSecurity
        ? {
            type: 'password',
            autoComplete: 'one-time-code',
            'data-1p-ignore': 'true',
            'data-bwignore': 'true',
            'data-lpignore': 'true',
          }
        : {
            type: 'text',
          };

    if (editable) {
      return (
        <TextField
          {...other}
          className={classNames(classes.inputField, this.props.className)}
          sharedcomponentid='AmountField'
          value={`${showAmountAdornment ? '' : `${currencyString}${currencyString ? ' ' : ''}`}${this.state.value}`}
          onChange={this.handleChange}
          autoFocus={autoFocus}
          label={label}
          margin={marginProp}
          onFocus={this.handleFocus}
          onBlur={this.onBlur}
          inputRef={this.setInputRef}
          onKeyDown={this.handleKeyPress}
          variant={textFieldVariant}
          {...privacyModeProps}
          InputProps={{
            ...this.props.InputProps,
            ...(disableUnderline && { disableUnderline: true }),
            inputProps: {
              'data-testid': 'amount-field-editable',
              className: classNames(
                classes.inputField,
                this.props.className,
                ...(isPrivacyMode && supportsWebkitTextSecurity ? [classes.privacyMask] : []),
              ),
              maxLength:
                maxChars ||
                13 + (currencyString ? currencyString.length : 0) + (showSign === ShowSignEnum.NEVER ? 0 : 1),
            },
            startAdornment: showAmountAdornment ? (
              <InputAdornment classes={{ root: classes.amountadornment }} disableTypography position='start'>
                {currencyString}
              </InputAdornment>
            ) : (
              ''
            ),
          }}
        />
      );
    }

    let colorClass;
    switch (showColor) {
      case ShowColorEnum.POSITIVE_ONLY:
        colorClass = value > 0 ? classes.green : undefined;
        break;
      case ShowColorEnum.NEGATIVE_ONLY:
        colorClass = value < 0 ? classes.red : undefined;
        break;
      case ShowColorEnum.ALWAYS:
        if (value < 0) {
          colorClass = classes.red;
        } else if (value > 0) {
          colorClass = classes.green;
        }
        break;
      case ShowColorEnum.MANUAL:
        colorClass = classes[color];
        break;
      default:
        assert(false, `unknown color mode '${showColor}'`);
    }
    return (
      <Wrap {...wrapProps}>
        <Typography
          data-testid='amount-field'
          {...other}
          variant={variant || 'inherit'}
          component='span'
          sharedcomponentid='AmountField'
          color={textPrimary ? 'textPrimary' : textSecondary ? 'textSecondary' : undefined}
          className={classNames(
            ...[classes.typographyField, this.props.className, ...(colorClass ? [colorClass] : [])],
          )}
        >
          {isPrivacyMode
            ? `${isPrivacyMode ? '' : '$'}***`
            : this.renderStaticValue(value, currencyString, omitCents, showSign)}
        </Typography>
      </Wrap>
    );
  }
}

AmountField.defaultProps = {
  amountType: 'amount',
  showColor: ShowColorEnum.MANUAL,
  textFieldVariant: 'filled',
  fractionDigits: 2,
};

const mapStateToProps = (state) => {
  const isPrivacyMode = selectPrivacyModeEnabled(state);
  return {
    isPrivacyMode,
  };
};

AmountField.propTypes = {
  name: PropTypes.string,
  align: PropTypes.string,
  autoComplete: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.any,
  error: PropTypes.any,
  id: PropTypes.string,
  isPrivacyMode: PropTypes.bool,
  editable: PropTypes.bool,
  className: PropTypes.string, // applied to root of textField, use 'classes.inputField' if you want to style the inner input
  style: PropTypes.object,
  InputProps: PropTypes.object,
  onKeyDown: PropTypes.func,
  zeroIsBlank: PropTypes.bool,
  onChange: PropTypes.func,
  amountType: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  classes: PropTypes.object,
  updateValue: PropTypes.string,
  useAbsoluteValue: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  autoFocus: PropTypes.bool,
  inputRef: PropTypes.func,
  thinFont: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  currencySymbol: PropTypes.string,
  currencyGap: PropTypes.bool,
  omitCents: PropTypes.bool,
  disabled: PropTypes.bool,
  proposedSign: PropTypes.number,
  lockInitialProposedSign: PropTypes.bool,
  showSign: PropTypes.string, // ShowSignEnum
  showColor: PropTypes.string, // ShowColorEnum
  color: PropTypes.string,
  disableUnderline: PropTypes.bool,
  submitOnBlur: PropTypes.bool,
  onSubmit: PropTypes.func,
  showAmountAdornment: PropTypes.bool,
  compact: PropTypes.bool,
  hideCurrencyString: PropTypes.bool,
  variant: PropTypes.string,
  textFieldVariant: PropTypes.string,
  marginProp: PropTypes.string,
  label: PropTypes.string,
  maxChars: PropTypes.number,
  fractionDigits: PropTypes.number,
  fractionCents: PropTypes.bool,
  textPrimary: PropTypes.bool,
  textSecondary: PropTypes.bool,
  fullWidth: PropTypes.bool,
  maxVal: PropTypes.number, //  Only works with positive values!
  minVal: PropTypes.number, //  Only works with negative values!
  dispatch: PropTypes.func,
};

export default connect(mapStateToProps)(withStyles(AmountField, styles));
